// features/seller/components/EditSellerModal.jsx

import { useEffect, useState } from 'react';
import { X, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useUpdateSellerProfile } from '../hooks/useUpdateSellerProfile';
import { useMainCategories } from 'features/main-category/hooks/useMainCategories';
import { Toaster } from 'sonner';


const SELLER_TYPES = [
  { key: 'service_provider', label: 'Owner' },
  { key: 'freelancer', label: 'Freelancer' },
  { key: 'staff', label: 'Staff' },
];

const Section = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className='border border-border rounded-lg overflow-hidden'>
      <button
        type='button'
        onClick={() => setOpen((p) => !p)}
        className='w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted transition-colors text-sm font-medium text-foreground'
      >
        {title}
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className='px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4'>{children}</div>}
    </div>
  );
};

const Field = ({ label, children, full = false }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <label className='block text-xs font-medium text-muted-foreground mb-1'>{label}</label>
    {children}
  </div>
);

const inputCls =
  'w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary text-foreground placeholder:text-muted-foreground';

const selectCls =
  'w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary text-foreground';

// Build initial form state from seller object
const buildInitialState = (seller) => ({
  firstName: seller?.firstName || '',
  lastName: seller?.lastName || '',
  email: seller?.email || '',
  phoneNumber: seller?.phoneNumber || '',
  dateOfBirth: seller?.dateOfBirth ? seller.dateOfBirth.split('T')[0] : '',
  gender: seller?.gender || '',
  about: seller?.about || '',
  sellerType: seller?.sellerType || '',
  isAgent: seller?.isAgent ?? false,
  willSellFood: seller?.willSellFood ?? false,
  restaurantName: seller?.restaurantName || '',
  categories: seller?.categories?._id || seller?.categories || '',
  // address
  addressLine1: seller?.address?.addressLine1 || '',
  addressLine2: seller?.address?.addressLine2 || '',
  landmark: seller?.address?.landmark || '',
  city: seller?.address?.city || '',
  state: seller?.address?.state || '',
  pincode: seller?.address?.pincode || '',
  country: seller?.address?.country || '',
  // bank
  accountHolderName: seller?.bankDetails?.accountHolderName || '',
  accountNumber: seller?.bankDetails?.accountNumber || '',
  ifscCode: seller?.bankDetails?.ifscCode || '',
  bankName: seller?.bankDetails?.bankName || '',
  branchName: seller?.bankDetails?.branchName || '',
  upiId: seller?.bankDetails?.upiId || '',
  // preferences
  language: seller?.preferences?.language || 'english',
  notifPush: seller?.preferences?.notifications?.push ?? true,
  notifEmail: seller?.preferences?.notifications?.email ?? true,
  notifSms: seller?.preferences?.notifications?.sms ?? true,
//   kyc
    kycAadhaarFront: seller?.kycDetails?.aadhaarFront || '',
    kycAadhaarBack: seller?.kycDetails?.aadhaarBack || '',
    kycPanCard: seller?.kycDetails?.panCard || '',
});

// Build the payload — only include fields that changed
const buildPayload = (form, initial) => {
  const payload = {};

  const basicFields = [
    'firstName', 'lastName', 'email', 'about', 'sellerType',
    'restaurantName', 'categories',
  ];
  basicFields.forEach((key) => {
    if (form[key] !== initial[key]) payload[key] = form[key];
  });

  if (form.dateOfBirth !== initial.dateOfBirth && form.dateOfBirth)
    payload.dateOfBirth = form.dateOfBirth;
  if (form.gender !== initial.gender && form.gender)
    payload.gender = form.gender;
  if (form.isAgent !== initial.isAgent)
    payload.isAgent = form.isAgent;
  if (form.willSellFood !== initial.willSellFood)
    payload.willSellFood = form.willSellFood;

  // address — send as a block if any address field changed
  const addrKeys = ['addressLine1', 'addressLine2', 'landmark', 'city', 'state', 'pincode', 'country'];
  const addrChanged = addrKeys.some((k) => form[k] !== initial[k]);
  if (addrChanged) {
    payload.address = {
      addressLine1: form.addressLine1,
      addressLine2: form.addressLine2,
      landmark: form.landmark,
      city: form.city,
      state: form.state,
      pincode: form.pincode,
      country: form.country,
    };
  }

  // bank — send as a block if any bank field changed
  const bankKeys = ['accountHolderName', 'accountNumber', 'ifscCode', 'bankName', 'branchName', 'upiId'];
  const bankChanged = bankKeys.some((k) => form[k] !== initial[k]);
  if (bankChanged) {
    payload.bankDetails = {
      accountHolderName: form.accountHolderName,
      accountNumber: form.accountNumber,
      ifscCode: form.ifscCode,
      bankName: form.bankName,
      branchName: form.branchName,
      upiId: form.upiId,
    };
  }

  // preferences — send as block if any changed
  const prefChanged =
    form.language !== initial.language ||
    form.notifPush !== initial.notifPush ||
    form.notifEmail !== initial.notifEmail ||
    form.notifSms !== initial.notifSms;
  if (prefChanged) {
    payload.preferences = {
      language: form.language,
      notifications: {
        push: form.notifPush,
        email: form.notifEmail,
        sms: form.notifSms,
      },
    };
  }

  return payload;
};

const EditSellerModal = ({ seller, onClose }) => {
  const [form, setForm] = useState(() => buildInitialState(seller));
  const [initial] = useState(() => buildInitialState(seller));


  const { mutate, isPending } = useUpdateSellerProfile();
  const { data: categoryResponse } = useMainCategories({ limit: 100 });
  const categories = categoryResponse?.data || [];

  // Reset form if seller changes (e.g. modal reused)
  useEffect(() => {
    setForm(buildInitialState(seller));
  }, [seller?._id]);

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [key]: value }));
  };

const handleSubmit = (e) => {
  e.preventDefault();

  const payload = buildPayload(form, initial);

  if (Object.keys(payload).length === 0) {
    toast.info('No changes to save');
    return;
  }

  mutate(
    {
      sellerId: seller._id,
      updates: payload,
    },
    {
      onSuccess: () => {
        onClose();
      },
    }
  );
};


// const aadhaarDoc = seller?.kyc?.documents?.find(
//   (doc) => doc.type === 'aadhaar'
// );

// const panDoc = seller?.kyc?.documents?.find(
//   (doc) => doc.type === 'pan'
// );


  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      <Toaster position="top-right" richColors />
      {/* Backdrop */}
      <div
        className='absolute inset-0 bg-black/60 backdrop-blur-sm'
        onClick={onClose}
      />

      {/* Modal */}
      <div className='relative z-10 w-full max-w-2xl mx-4 bg-card border border-border rounded-xl shadow-2xl flex flex-col max-h-[90vh]'>
        {/* Header */}
        <div className='flex items-center justify-between px-6 py-4 border-b border-border shrink-0'>
          <div>
            <h2 className='text-base font-semibold text-foreground'>Edit Seller Profile</h2>
            <p className='text-xs text-muted-foreground mt-0.5'>
              {seller?.firstName} {seller?.lastName} · {seller?.phoneNumber}
            </p>
          </div>
          <button
            onClick={onClose}
            className='p-1.5 hover:bg-muted rounded-md transition-colors text-muted-foreground hover:text-foreground'
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <form onSubmit={handleSubmit} className='flex-1 overflow-y-auto px-6 py-4 space-y-3' id="edit-seller-form">

          {/* Basic Info */}
          <Section title='Basic Information' defaultOpen>
            <Field label='First Name'>
              <input className={inputCls} value={form.firstName} onChange={set('firstName')} placeholder='First name' />
            </Field>
            <Field label='Last Name'>
              <input className={inputCls} value={form.lastName} onChange={set('lastName')} placeholder='Last name' />
            </Field>
            <Field label='Email'>
              <input className={inputCls} type='email' value={form.email} onChange={set('email')} placeholder='Email address' />
            </Field>
            <Field label='Phone Number'>
              <input
                className={`${inputCls} opacity-60 cursor-not-allowed`}
                value={form.phoneNumber}
                readOnly
                title={seller?.phoneVerified ? 'Phone verified — cannot be changed' : 'Phone number'}
              />
              {seller?.phoneVerified && (
                <p className='text-xs text-yellow-600 mt-1'>Phone is verified and locked.</p>
              )}
            </Field>
            <Field label='Date of Birth'>
              <input className={inputCls} type='date' value={form.dateOfBirth} onChange={set('dateOfBirth')} />
            </Field>
            <Field label='Gender'>
              <select className={selectCls} value={form.gender} onChange={set('gender')}>
                <option value=''>Select gender</option>
                <option value='male'>Male</option>
                <option value='female'>Female</option>
                <option value='other'>Other</option>
              </select>
            </Field>
            <Field label='About' full>
              <textarea
                className={`${inputCls} resize-none`}
                rows={3}
                value={form.about}
                onChange={set('about')}
                placeholder='Short bio...'
              />
            </Field>
          </Section>

          {/* Role & Business */}
          <Section title='Role & Business'>
            <Field label='Seller Type'>
              <select className={selectCls} value={form.sellerType} onChange={set('sellerType')}>
                <option value=''>Select type</option>
                {SELLER_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </Field>
            <Field label='Category'>
              <select className={selectCls} value={form.categories} onChange={set('categories')}>
                <option value=''>Select category</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id}>{c.name?.english}</option>
                ))}
              </select>
            </Field>
            <Field label='Restaurant Name'>
              <input className={inputCls} value={form.restaurantName} onChange={set('restaurantName')} placeholder='Restaurant name' />
            </Field>
            <Field label='Flags'>
              <div className='space-y-2 pt-1'>
                <label className='flex items-center gap-2 text-sm text-foreground cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={form.isAgent}
                    onChange={set('isAgent')}
                    className='w-4 h-4 rounded border-border accent-primary'
                  />
                  Is Agent
                </label>
                <label className='flex items-center gap-2 text-sm text-foreground cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={form.willSellFood}
                    onChange={set('willSellFood')}
                    className='w-4 h-4 rounded border-border accent-primary'
                  />
                  Will Sell Food
                </label>
              </div>
            </Field>
          </Section>

          {/* Address */}
          <Section title='Address'>
            <Field label='Address Line 1' full>
              <input className={inputCls} value={form.addressLine1} onChange={set('addressLine1')} placeholder='Street address' />
            </Field>
            <Field label='Address Line 2' full>
              <input className={inputCls} value={form.addressLine2} onChange={set('addressLine2')} placeholder='Apartment, suite, etc.' />
            </Field>
            <Field label='Landmark'>
              <input className={inputCls} value={form.landmark} onChange={set('landmark')} placeholder='Nearby landmark' />
            </Field>
            <Field label='City'>
              <input className={inputCls} value={form.city} onChange={set('city')} placeholder='City' />
            </Field>
            <Field label='State'>
              <input className={inputCls} value={form.state} onChange={set('state')} placeholder='State' />
            </Field>
            <Field label='Pincode'>
              <input className={inputCls} value={form.pincode} onChange={set('pincode')} placeholder='Pincode' />
            </Field>
            <Field label='Country'>
              <input className={inputCls} value={form.country} onChange={set('country')} placeholder='Country' />
            </Field>
          </Section>

          {/* Bank Details */}
          <Section title='Bank Details'>
            <Field label='Account Holder Name'>
              <input className={inputCls} value={form.accountHolderName} onChange={set('accountHolderName')} placeholder='Full name on account' />
            </Field>
            <Field label='Account Number'>
              <input className={inputCls} value={form.accountNumber} onChange={set('accountNumber')} placeholder='Account number' />
            </Field>
            <Field label='IFSC Code'>
              <input className={inputCls} value={form.ifscCode} onChange={set('ifscCode')} placeholder='IFSC code' />
            </Field>
            <Field label='Bank Name'>
              <input className={inputCls} value={form.bankName} onChange={set('bankName')} placeholder='Bank name' />
            </Field>
            <Field label='Branch Name'>
              <input className={inputCls} value={form.branchName} onChange={set('branchName')} placeholder='Branch name' />
            </Field>
            <Field label='UPI ID'>
              <input className={inputCls} value={form.upiId} onChange={set('upiId')} placeholder='UPI ID' />
            </Field>
          </Section>

          {/* Kyc */}
            {/* <Section title="KYC Documents">
    <Field label="Aadhaar Front">
      {aadhaarDoc?.documentUrl?.[0] && (
        <a
          href={aadhaarDoc?.documentUrl[0]}
          target="_blank"
          rel="noreferrer"
          className="text-blue-500 text-sm"
        >
          View Current Document
        </a>
      )}

      <input
        type="file"
        accept="image/*,.pdf"
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            aadhaarFrontFile: e.target.files?.[0],
          }))
        }
      />
    </Field>

    <Field label="Aadhaar Back">
      {aadhaarDoc?.documentUrl?.[1] && (
        <a
          href={aadhaarDoc?.documentUrl[1]}
          target="_blank"
          rel="noreferrer"
          className="text-blue-500 text-sm"
        >
          View Current Document
        </a>
      )}

      <input
        type="file"
        accept="image/*,.pdf"
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            aadhaarBackFile: e.target.files?.[0],
          }))
        }
      />
    </Field>

    <Field label="PAN Card">
      {panDoc?.documentUrl?.[0] && (
        <a
          href={panDoc.documentUrl[0]}
          target="_blank"
          rel="noreferrer"
          className="text-blue-500 text-sm"
        >
          View Current Document
        </a>
      )}

      <input
        type="file"
        accept="image/*,.pdf"
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            panFile: e.target.files?.[0],
          }))
        }
      />
    </Field>
  </Section> */}

          {/* Preferences */}
          <Section title='Preferences'>
            <Field label='Language'>
              <select className={selectCls} value={form.language} onChange={set('language')}>
                <option value='english'>English</option>
                <option value='hindi'>Hindi</option>
                <option value='marwari'>Marwari</option>
              </select>
            </Field>
            <Field label='Notifications'>
              <div className='space-y-2 pt-1'>
                {[
                  { key: 'notifPush', label: 'Push Notifications' },
                  { key: 'notifEmail', label: 'Email Notifications' },
                  { key: 'notifSms', label: 'SMS Notifications' },
                ].map(({ key, label }) => (
                  <label key={key} className='flex items-center gap-2 text-sm text-foreground cursor-pointer'>
                    <input
                      type='checkbox'
                      checked={form[key]}
                      onChange={set(key)}
                      className='w-4 h-4 rounded border-border accent-primary'
                    />
                    {label}
                  </label>
                ))}
              </div>
            </Field>
          </Section>

        </form>

        {/* Footer */}
        <div className='flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0'>
          <button
            type='button'
            onClick={onClose}
            disabled={isPending}
            className='px-4 py-2 text-sm border border-border rounded-md bg-card text-foreground hover:bg-muted transition-colors disabled:opacity-50'
          >
            Cancel
          </button>
          <button
            type='submit'
            form='edit-seller-form'
            disabled={isPending}
            // onClick={handleSubmit}
            className='flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50'
          >
            {isPending && <Loader2 size={14} className='animate-spin' />}
            {isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSellerModal;